import * as RTG from '@radix-ui/react-toggle-group'
import type { ReactNode } from 'react'
import { cn } from './cn'

export interface SegmentOption<T extends string = string> {
  value: T
  label: ReactNode
  icon?: ReactNode
  disabled?: boolean
  'aria-label'?: string
}

export interface SegmentedControlProps<T extends string = string> {
  options: SegmentOption<T>[]
  value: T
  onValueChange: (value: T) => void
  size?: 'sm' | 'md'
  fullWidth?: boolean
  className?: string
  'aria-label'?: string
}

/** Bordered segment group with one selected segment on a soft gray fill (Skool calendar view switch). */
export function SegmentedControl<T extends string = string>({
  options,
  value,
  onValueChange,
  size = 'md',
  fullWidth,
  className,
  'aria-label': ariaLabel,
}: SegmentedControlProps<T>) {
  return (
    <RTG.Root
      type="single"
      value={value}
      onValueChange={(v) => {
        if (v) onValueChange(v as T)
      }}
      aria-label={ariaLabel}
      className={cn(
        // Never wider than its container: long labels (German, Dutch) scroll sideways instead of overflowing.
        'inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-control border border-line bg-surface p-[2px]',
        '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        size === 'sm' ? 'h-8' : 'h-9',
        fullWidth && 'flex w-full',
        className,
      )}
    >
      {options.map((opt) => (
        <RTG.Item
          key={opt.value}
          value={opt.value}
          disabled={opt.disabled}
          aria-label={opt['aria-label']}
          className={cn(
            'inline-flex h-full shrink-0 items-center justify-center gap-1.5 rounded-[6px] px-3 text-meta font-medium whitespace-nowrap text-subtle',
            'transition-[color,background-color,box-shadow] duration-150 ease-out outline-none hover:text-text',
            'focus-visible:ring-3 focus-visible:ring-focus-ring data-[state=on]:bg-track data-[state=on]:font-semibold',
            'data-[state=on]:text-text disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4',
            fullWidth && 'flex-1',
          )}
        >
          {opt.icon}
          {opt.label}
        </RTG.Item>
      ))}
    </RTG.Root>
  )
}
